import React from 'react'
import { Scatter } from 'react-chartjs-2'
import { Box, Text } from '@/components/lib'
import SimpleBarChart from '@/components/lib/SimpleBarChart'
import SimpleLineChart, { CHART_COLOURS, useChartTheme } from '@/components/lib/SimpleLineChart'
import SimpleDoughnutChart from '@/components/lib/SimpleDoughnutChart'

// ---------------------------------------------------------------------------
// Flat row format helpers  {type, x, y, series?, data: [{...}, ...]}
// ---------------------------------------------------------------------------

function buildSingleDataset (data, x, y, yLabel) {
  return {
    labels: data.map(d => d[x]),
    datasets: [{ label: yLabel || y, data: data.map(d => d[y]) }]
  }
}

function buildSeriesDatasets (data, x, y, series) {
  const labels = [...new Set(data.map(d => d[x]))]
  const seriesValues = [...new Set(data.map(d => d[series]))]
  return {
    labels,
    datasets: seriesValues.map((sv, i) => ({
      label: sv,
      borderColor: CHART_COLOURS[i % CHART_COLOURS.length],
      data: labels.map(l => {
        const point = data.find(d => d[x] === l && d[series] === sv)
        return point !== undefined ? point[y] : null
      })
    }))
  }
}

// ---------------------------------------------------------------------------
// Table + number blocks (flat row format only)
// ---------------------------------------------------------------------------

function TableBlock ({ data, title }) {
  if (!data || data.length === 0) return <Text fontSize='sm' color='fg.muted'>No data</Text>
  const columns = Object.keys(data[0])
  return (
    <Box>
      {title && <Text fontWeight='semibold' fontSize='sm' mb={2}>{title}</Text>}
      <Box overflowX='auto'>
        <Box as='table' width='100%' fontSize='xs' style={{ borderCollapse: 'collapse' }}>
          <Box as='thead' bg='bg.muted'>
            <Box as='tr'>
              {columns.map(col => (
                <Box
                  key={col} as='th' px={2} py={1} textAlign='left' fontWeight='semibold'
                  borderBottom='1px solid' borderColor='border.default'
                >
                  {col}
                </Box>
              ))}
            </Box>
          </Box>
          <Box as='tbody'>
            {data.map((row, i) => (
              <Box key={i} as='tr' borderBottom='1px solid' borderColor='border.muted'>
                {columns.map(col => (
                  <Box key={col} as='td' px={2} py={1}>{row[col] ?? '—'}</Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function NumberBlock ({ data, title, x, y }) {
  const row = data?.[0]
  const value = row ? (row[y] ?? row[x] ?? Object.values(row)[0]) : null
  return (
    <Box textAlign='center' py={3}>
      {title && <Text fontSize='sm' color='fg.muted' mb={1}>{title}</Text>}
      <Text fontSize='4xl' fontWeight='bold'>{value ?? '—'}</Text>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AssistantChartBlock ({ children }) {
  let spec
  try {
    spec = JSON.parse(String(children).trim())
  } catch {
    return (
      <Box p={2} rounded='md' bg='bg.muted' border='1px solid' borderColor='border.default'>
        <Text fontSize='xs' color='fg.subtle'>Could not render chart — invalid JSON.</Text>
      </Box>
    )
  }

  const { type, title, x, y, series, xLabel, yLabel } = spec

  // Detect format:
  // Native Chart.js — spec.data = { labels: [...], datasets: [...] }
  // Flat rows       — spec.data = [{...}, ...] or { data: [{...},...] }
  const isNative = spec.data && !Array.isArray(spec.data) && Array.isArray(spec.data.labels)

  let labels, datasets, flatData

  if (isNative) {
    labels = spec.data.labels
    datasets = spec.data.datasets
  } else {
    flatData = Array.isArray(spec.data)
      ? spec.data
      : Array.isArray(spec.data?.data)
        ? spec.data.data
        : null

    if (!flatData || flatData.length === 0) {
      return (
        <Box p={2} rounded='md' bg='bg.subtle'>
          <Text fontSize='xs' color='fg.muted'>{title || 'Chart'}: no data returned.</Text>
        </Box>
      )
    }

    if (type === 'table') return <Box bg='bg.surface' rounded='md' border='1px solid' borderColor='border.default' p={3} my={2}><TableBlock data={flatData} title={title} /></Box>
    if (type === 'number') return <Box bg='bg.surface' rounded='md' border='1px solid' borderColor='border.default' p={3} my={2}><NumberBlock data={flatData} title={title} x={x} y={y} /></Box>

    try {
      ;({ labels, datasets } = series
        ? buildSeriesDatasets(flatData, x, y, series)
        : buildSingleDataset(flatData, x, y, yLabel))
    } catch (e) {
      return (
        <Box p={2} rounded='md' bg='bg.muted' border='1px solid' borderColor='border.default'>
          <Text fontSize='xs' color='fg.subtle'>Could not render chart — {e.message}</Text>
        </Box>
      )
    }
  }

  if (!labels || !datasets?.length) {
    return (
      <Box p={2} rounded='md' bg='bg.subtle'>
        <Text fontSize='xs' color='fg.muted'>{title || 'Chart'}: no data returned.</Text>
      </Box>
    )
  }

  // Merge any LLM-supplied options with axis label overrides
  const baseOptions = spec.options || {}
  const chartOptions = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      title: { display: !!title, text: title, ...baseOptions.plugins?.title }
    },
    ...(type !== 'pie' && type !== 'doughnut' && {
      scales: {
        ...baseOptions.scales,
        x: { title: { display: !!xLabel, text: xLabel }, ...baseOptions.scales?.x },
        y: { title: { display: !!yLabel, text: yLabel }, ...baseOptions.scales?.y }
      }
    })
  }

  // Call unconditionally to satisfy rules-of-hooks; used only for scatter below
  const scatterOptions = useChartTheme(chartOptions)

  if (type === 'scatter') {
    const scatterData = isNative
      ? spec.data
      : {
          datasets: [{ label: yLabel || y, data: flatData.map(d => ({ x: d[x], y: d[y] })), backgroundColor: CHART_COLOURS[0] }]
        }
    return (
      <Box bg='bg.surface' rounded='md' border='1px solid' borderColor='border.default' p={3} my={2}>
        <Scatter data={scatterData} options={scatterOptions} />
      </Box>
    )
  }

  return (
    <Box bg='bg.surface' rounded='md' border='1px solid' borderColor='border.default' p={3} my={2}>
      {type === 'bar' && <SimpleBarChart labels={labels} datasets={datasets} options={chartOptions} />}
      {type === 'line' && <SimpleLineChart labels={labels} datasets={datasets} options={chartOptions} />}
      {type === 'pie' && <SimpleDoughnutChart labels={labels} datasets={[{ data: datasets[0]?.data ?? datasets.map(d => d.data?.[0]) }]} options={chartOptions} />}
      {!['bar', 'line', 'pie'].includes(type) && (
        <Text fontSize='xs' color='fg.muted'>Unsupported chart type: {type}</Text>
      )}
    </Box>
  )
}
